import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';

import { useDebounce } from '../../core/utils/hooks/useDebounce.ts';
import { newestFilePerPattern } from '../files.ts';
import { GET_SFTP_DIRECTORY } from '../queries.ts';
import { SftpDirectory, SftpPatternFile } from '../types.ts';

const PATTERNS_DEBOUNCE_MS = 500;

// The newest file in the configured SFTP folder per pattern; the patterns may be unsaved, so they are debounced
const useSftpPatternFiles = (lookupDefinitionId: string, patterns: string[], skip: boolean) => {
    const [queriedPatterns, setQueriedPatterns] = useState(patterns);
    const setQueriedPatternsDebounced = useDebounce(setQueriedPatterns, PATTERNS_DEBOUNCE_MS);
    useEffect(() => {
        setQueriedPatternsDebounced(patterns);
    }, [patterns, setQueriedPatternsDebounced]);

    const { data, loading, error, refetch } = useQuery<{ sftpDirectory: SftpDirectory }>(GET_SFTP_DIRECTORY, {
        variables: { lookupDefinitionId, path: null, fileNamePatterns: queriedPatterns },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        skip,
    });

    const newestFiles = newestFilePerPattern(data?.sftpDirectory.entries ?? []);
    const patternFiles: SftpPatternFile[] = patterns.map((pattern) => ({
        pattern,
        file: newestFiles.find((file) => file.matchingPattern === pattern) ?? null,
    }));
    const isStale = queriedPatterns.join('\n') !== patterns.join('\n');

    return { path: data?.sftpDirectory.path, patternFiles, loading: loading || isStale, error, refetch };
};

export default useSftpPatternFiles;
